import React from 'react';
import { Layout } from '../components/Layout/Layout';
import { MarkdownRenderer } from '../components/Common/MarkdownRenderer';
import '../styles/page.css';

const cheatsheetContent = `# Markdown Cheatsheet

This guide covers standard markdown formatting and Mermaid diagram examples.

## Contents

## Text Formatting

### Emphasis

**Syntax:**
\`\`\`
*Italic text* or _italic text_
**Bold text** or __bold text__
***Bold and italic*** or ___bold and italic___
~~Strikethrough~~
\`\`\`

**Result:**
- *Italic text* or _italic text_
- **Bold text** or __bold text__
- ***Bold and italic*** or ___bold and italic___
- ~~Strikethrough~~

### Headings

**Syntax:**
\`\`\`
# Heading 1
## Heading 2
### Heading 3
#### Heading 4
##### Heading 5
###### Heading 6
\`\`\`

## Lists

### Unordered Lists

**Syntax:**
\`\`\`
- Item 1
- Item 2
  - Nested item 2.1
  - Nested item 2.2
- Item 3
\`\`\`

**Result:**
- Item 1
- Item 2
  - Nested item 2.1
  - Nested item 2.2
- Item 3

### Ordered Lists

**Syntax:**
\`\`\`
1. First item
2. Second item
   1. Nested item 2.1
   2. Nested item 2.2
3. Third item
\`\`\`

**Result:**
1. First item
2. Second item
   1. Nested item 2.1
   2. Nested item 2.2
3. Third item

### Task Lists

**Syntax:**
\`\`\`
- [x] Completed task
- [ ] Incomplete task
- [ ] Another task
\`\`\`

**Result:**
- [x] Completed task
- [ ] Incomplete task
- [ ] Another task

## Links and Images

### Table of Contents

Automatically generate a table of contents using the \`<!-- toc -->\` marker. This creates links to all headings in the document:

**Syntax:**
\`\`\`
## Table of Contents

<!-- toc -->
\`\`\`

**Result:** The table of contents will be automatically generated with links to all headings. Click any link to smoothly scroll to that section.

**Note:** The \`<!-- toc -->\` marker must be placed where you want the table of contents to appear. It works best when placed after a heading like "Table of Contents" or "On this page".

### Links

**Syntax:**
\`\`\`
[Link text](https://example.com)
[Link with title](https://example.com "Link Title")
\`\`\`

**Result:**

[Link text](https://example.com)

[Link with title](https://example.com "Link Title")

### Internal Document Links (Wiki-Style)

Link to other documents using wiki-style brackets. Start typing \`[[\` in the editor and autocomplete will suggest available documents:

**Syntax:**
\`\`\`
[[Document Title]]
\`\`\`

This automatically converts to a markdown link like \`[Document Title](/docs/document-slug)\` and navigates within the app when clicked.

You can also create internal links manually:

**Syntax:**
\`\`\`
[Link to another doc](/docs/my-other-document)
\`\`\`

**Note:** Internal document links (starting with \`/docs/\`) navigate within the app, while external links open in a new tab.

### Images

**Basic Syntax:**
\`\`\`
![Alt text](https://via.placeholder.com/150)
![Image with title](https://via.placeholder.com/150 "Image Title")
\`\`\`

**With Dimensions & Alignment:**

You can control image size and alignment using attributes inside the alt text:

\`\`\`
![Image{width=300}](url)
![Image{width=50%}](url)
![Image{height=200}](url)
![Image{width=500 height=300}](url)
![Image{align=center}](url)
![Image{width=400 align=right}](url)
![Image{width=300 height=200 align=center}](url)
\`\`\`

**Supported attributes:**
- \`width\` - Set image width (pixels or percentage)
- \`height\` - Set image height (pixels or percentage)
- \`align\` - Align image: \`left\`, \`center\`, or \`right\`

**Note:** If only width OR height is specified, aspect ratio is preserved. If both are specified, the image may distort.

**Result:**

![Alt text](https://via.placeholder.com/150)

![Centered image{width=300 align=center}](https://via.placeholder.com/300)

## Code

### Inline Code

**Syntax:**
\`\`\`
Use \`inline code\` within text.
\`\`\`

**Result:**

Use \`inline code\` within text.

### Code Blocks

**Syntax:**
\`\`\`\`
\`\`\`javascript
function greet(name) {
  console.log(\`Hello, \${name}!\`);
}
\`\`\`
\`\`\`\`

**Result:**
\`\`\`javascript
function greet(name) {
  console.log(\`Hello, \${name}!\`);
}
\`\`\`

## Quotes

**Syntax:**
\`\`\`
> This is a blockquote.
> It can span multiple lines.
>
> > Nested blockquote
\`\`\`

**Result:**
> This is a blockquote.
> It can span multiple lines.
>
> > Nested blockquote

## Tables

**Syntax:**
\`\`\`
| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Row 1    | Data     | More data|
| Row 2    | Data     | More data|
\`\`\`

**Result:**

| Column 1 | Column 2 | Column 3 |
|----------|----------|----------|
| Row 1    | Data     | More data|
| Row 2    | Data     | More data|

### Table Alignment

**Syntax:**
\`\`\`
| Left aligned | Center aligned | Right aligned |
|:-------------|:--------------:|--------------:|
| Left         | Center         | Right         |
\`\`\`

**Result:**

| Left aligned | Center aligned | Right aligned |
|:-------------|:--------------:|--------------:|
| Left         | Center         | Right         |

## Horizontal Rule

**Syntax:**
\`\`\`
---
\`\`\`

**Result:**

---

**Or use:**
\`\`\`
***
\`\`\`

## Mermaid Diagrams

Mermaid lets you create diagrams using text. Just use a code block with \`mermaid\` as the language.

### Flowchart

**Syntax:**
\`\`\`\`
\`\`\`mermaid
graph TD
    A[Start] --> B{Is it working?}
    B -->|Yes| C[Great!]
    B -->|No| D[Debug]
    D --> B
    C --> E[End]
\`\`\`
\`\`\`\`

**Result:**

\`\`\`mermaid
graph TD
    A[Start] --> B{Is it working?}
    B -->|Yes| C[Great!]
    B -->|No| D[Debug]
    D --> B
    C --> E[End]
\`\`\`

### Sequence Diagram

**Syntax:**
\`\`\`\`
\`\`\`mermaid
sequenceDiagram
    participant User
    participant Browser
    participant Server
    
    User->>Browser: Enter URL
    Browser->>Server: HTTP Request
    Server-->>Browser: HTTP Response
    Browser-->>User: Display Page
\`\`\`
\`\`\`\`

**Result:**

\`\`\`mermaid
sequenceDiagram
    participant User
    participant Browser
    participant Server
    participant Database
    
    User->>Browser: Enter URL
    Browser->>Server: HTTP Request
    Server->>Database: Query Data
    Database-->>Server: Return Data
    Server-->>Browser: HTTP Response
    Browser-->>User: Display Page
\`\`\`

### Class Diagram

**Syntax:**
\`\`\`\`
\`\`\`mermaid
classDiagram
    class Animal {
        +String name
        +int age
        +makeSound()
    }
    class Dog {
        +String breed
        +bark()
    }
    Animal <|-- Dog
\`\`\`
\`\`\`\`

**Result:**

\`\`\`mermaid
classDiagram
    class Animal {
        +String name
        +int age
        +makeSound()
    }
    class Dog {
        +String breed
        +bark()
    }
    class Cat {
        +String color
        +meow()
    }
    Animal <|-- Dog
    Animal <|-- Cat
\`\`\`

### Gantt Chart

**Syntax:**
\`\`\`\`
\`\`\`mermaid
gantt
    title Project Timeline
    dateFormat  YYYY-MM-DD
    section Planning
    Research    :a1, 2024-01-01, 30d
    Design      :a2, after a1, 20d
    section Development
    Coding      :b1, 2024-02-01, 45d
\`\`\`
\`\`\`\`

**Result:**

\`\`\`mermaid
gantt
    title Project Timeline
    dateFormat  YYYY-MM-DD
    section Planning
    Research           :a1, 2024-01-01, 30d
    Requirements       :a2, after a1, 20d
    section Development
    Frontend           :b1, 2024-02-01, 45d
    Backend            :b2, 2024-02-15, 45d
    section Testing
    QA Testing         :c1, after b1, 15d
    Deployment         :c2, after c1, 5d
\`\`\`

### State Diagram

**Syntax:**
\`\`\`\`
\`\`\`mermaid
stateDiagram-v2
    [*] --> Idle
    Idle --> Processing: Start
    Processing --> Success: Complete
    Processing --> Error: Fail
    Success --> [*]
    Error --> Idle: Retry
\`\`\`
\`\`\`\`

**Result:**

\`\`\`mermaid
stateDiagram-v2
    [*] --> Idle
    Idle --> Processing: Start
    Processing --> Success: Complete
    Processing --> Error: Fail
    Success --> [*]
    Error --> Idle: Retry
    Error --> [*]: Cancel
\`\`\`

### Entity Relationship Diagram

**Syntax:**
\`\`\`\`
\`\`\`mermaid
erDiagram
    USER ||--o{ DOCUMENT : creates
    USER {
        int id
        string username
        string email
    }
    DOCUMENT {
        int id
        string title
        int author_id
    }
\`\`\`
\`\`\`\`

**Result:**

\`\`\`mermaid
erDiagram
    USER ||--o{ DOCUMENT : creates
    USER {
        int id
        string username
        string email
    }
    DOCUMENT {
        int id
        string title
        string content
        int author_id
    }
    DOCUMENT ||--o{ COMMENT : has
    COMMENT {
        int id
        string text
        int user_id
        int document_id
    }
    USER ||--o{ COMMENT : writes
\`\`\`

## Embedding Flowcharts

You can embed interactive flowcharts in your markdown documents using wiki-style syntax. Use double brackets with \`flowchart:\` prefix followed by your flowchart's name or slug:

**Syntax:** Use double square brackets with the flowchart prefix:
\`\`\`
[[flowchart:Your Flowchart Name]]
\`\`\`
Replace \`Your Flowchart Name\` with the actual name or slug of your flowchart (matching is case-insensitive).

**Example:**
If you have a flowchart named "My Process Flow", you would write:
\`\`\`
[[flowchart:My Process Flow]]
\`\`\`
**Result:** The flowchart will be rendered as an interactive, read-only diagram that users can pan and zoom.

**Note:** To create and edit flowcharts, go to the Flowcharts section in the sidebar. Flowcharts are stored separately from documents and can be embedded in multiple documents.

## Tips

- Use backticks (\\\`) for inline \`code\`
- Use triple backticks (\\\`\\\`\\\`) for code blocks
- Add language identifier after opening backticks for syntax highlighting
- Use \`mermaid\` identifier for diagram code blocks
- Leave blank lines between different elements for better rendering
- For tables, colons control alignment: \`:---\` (left), \`:---:\` (center), \`---:\` (right)
`;

export const MarkdownCheatsheetPage: React.FC = () => {
  return (
    <Layout showSidebar={false}>
      <div className="page-container">
        <div className="markdown-content">
          <MarkdownRenderer content={cheatsheetContent} />
        </div>
      </div>
    </Layout>
  );
};
